/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import { cloneDeep, isEqual, merge, uniqueId } from 'lodash-es';
import QueryString from 'qs';
import actions from '../../../api/apiaApi/actions';
import ApiaApi, { makeApiaUrl } from '../../../api/apiaApi/apiaApi';
import globalStore from '../../../store';
import {
  IFileUploadState,
  uploaderActions,
} from '../../../store/fileUploadSlice';
import { remoteModalsActions } from '../../../store/remoteModalsSlice';
import { hasSucceedFormValidation } from '../../../store/validation/util';
import outerValidationsStore from '../../../store/validation/validationStore';
import {
  TApiaFunction,
  TApiaLoad,
  TFileUploaded,
  TRequireOnlyOne,
} from '../../../types';
import { addBoundary, parseAsSize } from '../../../utils/number';
import {
  arrayOrArray,
  downloadUrl,
  formatMessage,
  getLabel,
  toBoolean,
} from '../../../utils/util';
import {
  documentUploadModalId,
  TUploadModalStore,
} from '../../apia/common/modals/documentUploadModal';
import { translateModalId } from '../../apia/common/modals/translateModal/TranslateModal';
import { verifyModalEmitter } from '../../apia/execution/digitalSign/VerifySignModal';
import {
  forceScreenLock,
  releaseScreenLock,
} from '../dialogs/modals/ScreenLock';
import {
  IPoolsPermissions,
  IUsersPermissions,
  TAjaxUploadFileStatus,
  TAjaxUploadStart,
  TConfirmDropModal,
  TDocType,
  TDocumentAdditionalMetadata,
  TDocumentInformation,
  TDocumentMetadata,
  TProcessDroppedFiles,
  TSaveDroppedFilesConf,
  TUploaderConfigurationObject,
  TUploaderFileInfo,
  TUploaderLoadCurrentFunction,
  TUploaderLoadCurrentFunctionMessages,
} from './types';
import getConf from '../../../classes/configController';
import debugDispatcher from '../../../utils/debugDispatcher';
import { uploadModalMethods } from '../../apia/common/modals/documentUploadModal/ModalRenderer';
import { ApiaUtil, TDispatchedNotification } from '@apia/components';
import { EventEmitter, globals } from '@apia/util';

export type TOnUploadProgress = (progress: number) => void;

declare global {
  interface Window {
    docTypePerEntId: number;
    docTypePerProId: number;
    entityDocumentsModalConfiguration: TUploaderConfigurationObject;
    IS_READONLY: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ITHit: any;
    LBL_DROP_FILE_REPEATED: string;
    LBL_EDIT: string;
    MSG_FAIL_SYNC_DOCUMENT: string;
    MSG_NO_DOC_EDIT_PROTOCOL: string;
    MSG_NO_VALID_FILE: string;
    ONE_CLICK_UPLOAD: boolean;
    processDocumentsModalConfiguration: TUploaderConfigurationObject;
    WEBDAV_SERVER: string;
    DOC_LANGS: Record<number, string>;
    DOCUMENT_EVERYONE_PERMISSION: string | false;
  }
}

const defaultConfirmModalData = {
  docDesc: '',
  docExpDate: '',

  docFolder: -1,

  docAllowAllType: 'M',
};

export const uploaderNotificationsCategory = 'uploaderApi';

declare global {
  interface Window {
    avoidOneClickUpload: boolean;
    forceOneClickUpload: boolean;
  }
}

debugDispatcher.on(
  'oneClick',
  ([isEnabled]) => {
    if (isEnabled === true) {
      window.avoidOneClickUpload = false;
      window.forceOneClickUpload = true;
    } else if (isEnabled === false) {
      window.avoidOneClickUpload = true;
      window.forceOneClickUpload = false;
    } else {
      window.avoidOneClickUpload = false;
      window.forceOneClickUpload = false;
    }
  },
  'boolean | string',
);

/**
 * Toma en cuenta la propiedad y algunas variables de debug:
 *
 * window.avoidOneClickUpload = true, evita que se use el oneClickUpload
 * window.forceOneClickUpload = true, hace que todo tenga oneClickUpload
 *
 * window.avoidOneClickUpload > window.forceOneClickUpload
 */
export function isOneClickUploadEnabled(oneClickUploadProp?: boolean) {
  return (
    !window.avoidOneClickUpload &&
    (window.forceOneClickUpload || oneClickUploadProp || false)
  );
}

export function isTUploaderFileInfo(
  obj: TFileUploaded | TUploaderFileInfo,
): obj is TUploaderFileInfo {
  const pObj = obj as TUploaderFileInfo;

  return (
    pObj.isLocked !== undefined &&
    pObj.isLockedByMe !== undefined &&
    pObj.isMarkedToSign !== undefined &&
    pObj.isVirtual !== undefined &&
    pObj.lockingUser !== undefined
  );
}

/**
 * Toma la definición de archivos de Apia y lo convierte
 * al del cliente.
 */
export const parseFileDefinition = (
  fileDefinition: TFileUploaded | TUploaderFileInfo,
  isSignRequired: boolean,
): TUploaderFileInfo => {
  const { lock, locked, lockedBy, userLocking, ...file } =
    fileDefinition as TFileUploaded;

  return {
    ...file,
    isLocked: !!lockedBy || String(lock || locked) === 'true',
    isLockedByMe: (lockedBy || userLocking) === window.CURRENT_USER_LOGIN,
    isMarkedToSign: file.markedToSign || toBoolean(file.sign) || isSignRequired,
    isVirtual: file.virtualDoc ?? false,
    lockingUser: lockedBy || userLocking,
  };
};

/**
 * El comportamiento de esta función se explica por su
 * nombre.
 */
export function returnExactlyTheSame(
  defaultParameters: Record<string, unknown>,
): Record<string, unknown> {
  return defaultParameters;
}

/**
 * Para lograr un Api lo suficientemente flexible, necesito que los parámetros
 * que se pasan al cuerpo de los requests sea modificable. Para ello se van a
 * usar las funciones getXxxParameters donde Xxx representa el nombre del
 * request cuyos parámetros son necesarios modificar.
 *
 * También es necesario modificar algunas respuestas, por ejemplo al eliminar
 * los documentos, si es un documento de entidad o proceso se debe hacer un if
 * (result?.data?.function.name === 'fncDocumentRemove') pero si es un
 * documento de formulario se debe hacer if(result?.data?.success === true). En
 * este tipo de situaciones voy a usar los métodos con nombre
 * evaluateXxxResult, donde Xxx representa el método cuya respuesta será
 * sobreescrita.
 */

type TOverride = {
  type: 'E' | 'P';
  props: Partial<TUploaderConfigurationObject>;
};

const configurationObjectEmitter =
  new (class ConfigurationObjectEmitter extends EventEmitter<{
    override: TOverride;
  }> {})();
const help =
  globals?.getVersion()?.env === 'development'
    ? `dd.override({
    type: 'E', 
    props: {
      allowDocumentsMonitor: boolean;
      allowPickFromDirectories: boolean;
      collapsePermissions: boolean;
      collapseDirectories: boolean;
      collapseMetadata: boolean;
      monitorId?: number;
      oneClickUpload: boolean;
      showDirectoriesStructure: boolean;
      showExpDate: boolean;
      showPermissions: boolean;
      showMetadata: boolean;
    }
})`
    : '';
debugDispatcher.on(
  'override',
  ([conf]) => {
    configurationObjectEmitter.emit('override', conf as TOverride);
  },
  help,
);

export const uploaders: Record<string, UploaderApi> = {};
(window as any).up = uploaders;

export default class UploaderApi extends EventEmitter<{ fileUploaded: null }> {
  allowTranslations = false;

  langs: Record<number, string> | undefined;

  configurationObject: TUploaderConfigurationObject;

  currentConfiguration: TUploaderLoadCurrentFunctionMessages | undefined;

  shoutSuccessAlert = getConf('components.uploader.showSuccessMessage', true);

  #docTypePermittedObjId = 1;
  get docTypePermittedObjId() {
    return this.#docTypePermittedObjId;
  }
  set docTypePermittedObjId(value: number) {
    this.#docTypePermittedObjId = value;
  }

  constructor(
    type: string,
    public id = uniqueId(),
    isInstance = true,
  ) {
    super();

    this._type = type;
    this.allowTranslations = type === 'E' && !!window.DOC_LANGS;
    this.langs = window.DOC_LANGS;

    globalStore.dispatch(uploaderActions.create({ id: this.id }));

    try {
      this.docTypePermittedObjId =
        type === 'E' ? window.docTypePerEntId : window.docTypePerProId;
    } catch (e) {
      console.warn(e);
    }

    this.configurationObject =
      type === 'E'
        ? window.entityDocumentsModalConfiguration
        : window.processDocumentsModalConfiguration;

    if (isInstance) {
      configurationObjectEmitter.on('override', (params) => {
        if (params.type === type) {
          this.configurationObject = merge(
            this.configurationObject,
            params.props,
          );
        }
      });
    }

    uploaders[id] = this;
  }

  #lastUploadKnownConf: TAjaxUploadStart | null = null;

  /**
   * Este método debe es llamado al iniciar la página
   * y al abrir el modal. En ambos casos su utilidad
   * es la de recuperar el listado de tipos de archivos.
   */
  async ajaxUploadStart(newFiles: File[] = []) {
    if (window.IN_MONITOR) {
      return;
    }

    const result = await ApiaApi.post<TAjaxUploadStart>(
      makeApiaUrl(
        this.getAjaxUploadStartParameters({
          action: 'ajaxUploadStart',
          isAjax: true,
          docId: this.getValidationStore().versioningFile?.docId,
          prefix: this._type,
          docTypeId:
            newFiles.length === 1
              ? this.getValidationStore().versioningFile?.docTypeId || '1'
              : '1',
          newDoc: !this.getValidationStore().versioningFile,
          useDocTypePermitted: true,
          docTypePermittedObjType: this._type,
          docTypePermittedObjId: this.docTypePermittedObjId,
          ajaxUrl: this.getAjaxUrl(),
        }),
      ),
      {
        notificationsCategory: uploaderNotificationsCategory,
      },
    );

    if (result?.data) {
      this.#lastUploadKnownConf = result.data;
      this.resetModal();

      const docTypes = arrayOrArray(result.data.function.docTypes.docType);
      const hasGeneric = !!docTypes.find((c) => String(c.id) === '1');

      this.reloadMetadata({
        docTypeId: hasGeneric ? '1' : docTypes[0]?.id,
      });

      this.#hasInited;
    }

    if (newFiles.length > 0) {
      await this.saveDroppedFiles(newFiles, (progress) => {
        console.log(progress);
      });
    }

    return result?.data?.function.name === 'fncDocumentLoadInformation';
  }

  /**
   * Este método informa al usuario que la acción que
   * intenta realizar requiere que el documento está bloqueado
   * y pregunta si desea proceder a bloquearlo, de forma
   * de evitar que el usuario tenga que presionar el botón
   * bloquear antes de realizar nuevamente la acción.
   */
  async autoLock(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      resolve(false);
    });
  }

  /**
   * Permite determinar si un archivo está bloqueado
   * por otro usuario, admite un segundo parámetro
   * que determina si en caso de estar bloqueado se
   * debe emitir una notificación o no.
   *
   * Devuelve true en caso de que el documento haya
   * podido bloquear o false | string en caso de falla.
   *
   * El string devuelto al fallar es el nombre del
   * usuario que está bloqueando el documento.
   */
  async checkLockDocument(id: string, shouldNotifyUnlocked = true) {
    if (window.IN_MONITOR) {
      return false;
    }

    const file = this.indexedFiles[id];
    if (Number(file.docId) < 0) return true;

    const result = await ApiaApi.post<{
      success: 'ok' | 'unlocked';
      usr: string;
    }>(
      makeApiaUrl(
        this.getCheckLockDocumentParameters({
          action: 'checkLockDocument',
          docId: file.docId,
          ajaxUrl: this.getAjaxUrl(),
          prefix: this.type,
        }),
      ),
      {
        notificationsCategory: uploaderNotificationsCategory,
      },
    );

    const isLocked = result?.data?.success === 'ok';
    if (
      !isLocked &&
      result?.data?.usr &&
      result.data.usr !== window.CURRENT_USER_LOGIN
    ) {
      this.notify({
        message: `${window.MSG_DOC_LOCKED_BY_USR.split('.')[0]}: ${
          result.data.usr
        }.`,
        type: 'warning',
      });
      return result.data.usr;
    }
    if (!isLocked && shouldNotifyUnlocked && !(await this.autoLock()))
      this.notify({
        message: window.MSG_DOC_MUST_BE_LOCKED,
        type: 'warning',
      });

    return isLocked;
  }

  /**
   * Abre el modal de verificación de firma para el archivo que se desea
   * comprobar.
   */
  checkSignature(file: TUploaderFileInfo) {
    if (Number(file.docId) >= 0)
      verifyModalEmitter.emit('show', {
        url: makeApiaUrl(
          this.getCheckSignatureParameters({
            action: 'viewDocSigns',
            docId: file.docId,
            lock: false,
            isAjax: true,
            prefix: this.type,
            react: true,
          }),
        ),
      });
  }

  /**
   * Determina si el archivo está bloqueado por webDav.
   * En caso de estar bloqueado emite una notificación.
   *
   * Devuelve true en caso de poder bloquear o false
   * en caso contrario.
   */
  async checkWebDavLock(docId: string) {
    if (window.IN_MONITOR) {
      return false;
    }

    return new Promise((resolve) => {
      let tries = 10;
      const checkLockAction = async () => {
        const isLocked = await ApiaApi.post<{ locked: boolean }>(
          makeApiaUrl(
            this.getCheckWebDavLockParameters({
              action: 'isWebDavDocumentLocked',
              docId,
              ajaxUrl: window.URL_REQUEST_AJAX,
            }),
          ),
          {
            notificationsCategory: uploaderNotificationsCategory,
          },
        );
        if (
          isLocked?.data?.locked === true ||
          isLocked?.data?.locked === undefined
        ) {
          if (--tries > 0) {
            forceScreenLock();
            setTimeout(() => void checkLockAction(), 1000);
          } else {
            releaseScreenLock();
            this.notify({ message: window.MSG_FAIL_SYNC_DOCUMENT });
            resolve(false);
          }
        } else {
          resolve(true);
          releaseScreenLock();
        }
      };
      void checkLockAction();
    });
  }

  /**
   * Desde este punto en adelante están las implementaciones de los métodos de
   * la clase. Todos ellos pueden ser reescritos por las clases que extiendan a
   * UploaderApi. Sin embargo, en la mayoría de los casos alcanza con
   * sobreescribir los getters o los evaluate.
   */
  protected clearTempFiles() {
    /* const canceledFiles = this.getStore().inProgressFiles.map(
      (current) => current.name,
    );
    void ApiaApi.post(
      makeApiaUrl(
        this.getClearTempFilesParameters({
          ajaxUrl: this.getAjaxUrl(),
          action: 'clearTempFiles',
          elemType: this.type,
          canceledFiles,
        }),
      ),
    ); */
  }

  /**
   * Cuando se quiere confirmar la subida de archivos
   * al servidor, se debe llamar a este método.
   */
  async confirmDropModal(conf?: {
    langId?: number;
    translatingFile?: TUploaderFileInfo;
  }) {
    if (window.IN_MONITOR) {
      return;
    }

    const { langId, translatingFile } = conf ?? {};
    const store = this.getStore();
    const { cmbDocType, fromDirectoryFile } = this.getValidationStore();
    const { validationResult } = this.getActualValidationStore();

    if (validationResult && !hasSucceedFormValidation(validationResult)) return;

    const {
      docAllowAllType,
      docDesc,
      docExpDate,
      docFolder,
      poolsPermissions,
      usersPermissions,
      metadata,
    } = {
      ...defaultConfirmModalData,
      ...{
        metadata: [],
        poolsPermissions: [],
        usersPermissions: [],
      },
      ...validationResult?.submitValues,
    };

    const additionalMetadata =
      (validationResult?.submitValues as TUploadModalStore | undefined)
        ?.additionalMetadata ?? {};

    let postData = QueryString.stringify(
      this.getConfirmDropModalPostdata({
        cmbDocType,
        docDesc,

        docFolder: docFolder >= 0 ? docFolder : '',

        docAllowAllType,

        pe: this.docTypePermittedObjId?.toString(),

        dropped: store.inProgressFiles.map((file) => file.name),

        txtLangId: langId,

        txtLangGroup: translatingFile?.docLangGroup,
      }),
      { arrayFormat: 'repeat' },
    );

    postData = `${postData}${
      postData.length > 0 ? '&' : ''
    }${this.getConfirmDropModalPermissionsString({
      poolsPermissions,
      usersPermissions,
    })}&txtFreeMetadata=${this.getConfirmDropModalAdditionalMetadataString(
      additionalMetadata,
    )}&txtMetadata=${this.getConfirmDropModalMetadataString({
      metadata: Object.values(metadata || {}),
    })}&docExpDate=${docExpDate ?? ''}`;

    const result = await ApiaApi.post<TConfirmDropModal>(
      makeApiaUrl(
        this.getConfirmDropModalParameters({
          ajaxUrl: this.getAjaxUrl(),
          docId: fromDirectoryFile
            ? fromDirectoryFile.docId
            : this.getValidationStore().versioningFile?.docId,
          action: fromDirectoryFile
            ? actions.uploader.associateExistingFile
            : 'confirmDropModal',
          elemType: this.type,
          fromForm: true,
          ...(fromDirectoryFile
            ? {
                docId: fromDirectoryFile.docId,
                fromReuse: true,
                uploadDocument: false,
              }
            : null),
        }),
      ),
      {
        postData: postData.toString(),
        notificationsCategory: uploaderNotificationsCategory,
      },
    );

    if (result?.data?.function) {
      let currentFilesArray = this.filesActions.getAllAsArray();
      const { versioningFile } = this.getValidationStore();
      if (versioningFile) {
        currentFilesArray = currentFilesArray.filter(
          (current) =>
            (current.docName || current.name) !== versioningFile?.name,
        );
      }
      const newFilesArray = arrayOrArray(result.data.function.general)
        .map((current) =>
          this.parseFileDefinition({
            ...current,
            canWrite: true,
            canRead: true,
            canEdit: true,
          }),
        )
        .filter(
          (current) =>
            !currentFilesArray.find((search) => search.docId === current.docId),
        );

      if (langId && translatingFile)
        this.setTranslationFile(
          newFilesArray[0],
          translatingFile.docId,
          langId,
        );
      else
        this.filesActions.updateArray([...currentFilesArray, ...newFilesArray]);

      this.emit('fileUploaded', null);
      this.onCloseModal();
    }
  }

  async deleteDocument(id: string, langId?: number) {
    const file = this.indexedFiles[id];
    if ((await this.checkLockDocument(file.docId)) === true) {
      if (
        await ApiaUtil.instance.dialogs.confirm({
          children:
            !langId &&
            Object.values(this.getStore().translatedFiles[id] ?? {}).length > 0
              ? getLabel('msgDelFileTrans').text
              : window.MSG_CONFIG_DELETE_DOCUMENT_FILE_INPUT_NAME.replace(
                  '<TOK>',
                  file.docName ?? file.name ?? 'NONAME',
                ),
          title: window.BTN_FILE_ERASE_LBL,
        })
      ) {
        const result = await ApiaApi.post(
          makeApiaUrl(
            this.getDeleteDocumentParameters({
              action: 'ajaxRemoveDocument',
              docId: file.docId,
              isAjax: true,
              prefix: this.type,
              ajaxUrl: this.getAjaxUrl(),
              langId,
            }),
          ),
          {
            notificationsCategory: uploaderNotificationsCategory,
          },
        );

        if (this.evaluateDeleteDocumentResult(result?.data))
          this.filesActions.delete(file);
      }
    }
  }

  async downloadDocument(id: string) {
    const file = this.indexedFiles[id];
    if (window.IN_MONITOR || (await this.checkWebDavLock(file.docId))) {
      await downloadUrl(
        makeApiaUrl({
          action: 'downloadDocument',
          prefix: this.type,
          docId: file.downloadDocId,
          ajaxUrl: this.getAjaxUrl(),
        }),
      );
    }
  }

  /**
   * Por el momento realiza la descarga de todos los archivos
   * que estén subidos.
   */
  downloadMultipleDocuments() {
    const store = this.getStore();
    const files = store.filesArray;
    if (files.length === 0) return;
    const docIds = store.selectedFiles;

    const selectedFiles = files.filter((current) =>
      docIds.find((search) => search === current.docId),
    );
    const ids = (selectedFiles.length > 0 ? selectedFiles : files)
      .map((current) => current.downloadDocId)
      .join('-');

    void downloadUrl(
      makeApiaUrl(
        this.getDownloadMultipleDocumentsParameters({
          action: 'multipleDownload',
          docId: ids,
          prefix: this._type,
        }),
      ),
      'documents.zip',
    );
  }

  /**
   * Acciona el webDav para la edición del archivo.
   */
  async editDocument(id: string) {
    if (window.IN_MONITOR) {
      return;
    }

    const file = this.indexedFiles[id];
    if ((await this.checkLockDocument(file.docId)) === true) {
      const result = await ApiaApi.post<{ folderId: string }>(
        makeApiaUrl(
          this.getEditDocumentParameters({
            action: 'updateWebDavDocument',
            docId: file.docId,
            prefix: this.type,
            isAjax: true,
          }),
        ),
        {
          notificationsCategory: uploaderNotificationsCategory,
        },
      );
      if (result?.data?.folderId) {
        const sDocumentUrl = `${window.WEBDAV_SERVER}${
          result?.data?.folderId
        }/${file.docId}/${file.name || file.docName || ''}`;

        const ProtocolInstallMessage = () => {
          ApiaUtil.instance.notifications.notify({
            message: window.MSG_NO_DOC_EDIT_PROTOCOL,
            title: window.LBL_EDIT,
          });
        };

        window.avoidLoadEvents = true;
        window.ITHit.WebDAV.Client.DavConstants.ProtocolTimeoutMs = 10000;

        await window.ITHit.WebDAV.Client.DocManager.DavProtocolEditDocument(
          sDocumentUrl,
          window.WEBDAV_SERVER,
          ProtocolInstallMessage,
          null,
          'Current',
          'ApiaWebDavCookie', // Cookie(s) to copy.
          '', // URL to navigate to if any cookie from the list is not found.
          'Edit',
        );
      }
    }
  }

  /**
   * Los métodos que comienzan en evaluate están ideados para ser reemplazados
   * por aquellas clases que pretendan implementar un uploader distinto al de
   * documentos de proceso y documentos de entidad.
   *
   * Su función es evaluar una situación para saber si se debe proceder con el
   * flujo esperado o si hubo algún error.
   */
  protected evaluateDeleteDocumentResult(result: unknown) {
    return (
      (
        result as
          | undefined
          | TApiaLoad<
              TApiaFunction<
                {
                  general: {
                    docId: string;
                    prefix: string;
                  };
                },
                false
              >
            >
      )?.function?.name === 'fncDocumentRemove'
    );
  }

  /**
   * filesActions es un conjunto de acciones referentes al listado de archivos
   * ya subidos. No se debe confundir con el listado de archivos que están
   * siendo subidos por el modal.
   */
  get filesActions() {
    return {
      add: (files: TUploaderFileInfo | TUploaderFileInfo[]) => {
        globalStore.dispatch(
          uploaderActions.updateArray({
            id: this.id,
            files: [...this.filesArray, ...arrayOrArray(files)],
          }),
        );
      },
      delete: (file: TUploaderFileInfo) => {
        globalStore.dispatch(
          uploaderActions.deleteDocument({
            id: this.id,
            docId: file.docId,
          }),
        );
      },
      getAllAsArray: () => {
        return this.filesArray;
      },
      getAllIndexed: () => {
        return this.indexedFiles;
      },
      update: (file: TUploaderFileInfo) => {
        globalStore.dispatch(
          uploaderActions.updateFile({ id: this.id, ...file }),
        );
      },
      updateArray: (files: TUploaderFileInfo[]) => {
        if (!isEqual(files, this.getStore().filesArray))
          globalStore.dispatch(
            uploaderActions.updateArray({ id: this.id, files }),
          );
      },
    };
  }

  /**
   * Devuelve el listado de archivos ya subidos al
   * servidor y confirmados. No se debe confundir
   * con el listado de archivos en progreso de ser
   * subidos en el modal.
   */
  get filesArray() {
    return globalStore.getState().fileUploadSlice.uploaders[this.id].filesArray;
  }

  /**
   * Toma un array de archivos provenientes de un Dropzone y los filtra contra
   * los tipos admitidos, según el parámetro docTypeId sea provisto o no. En
   * caso de que uno o más archivos no pasen la validación, se devuelve un
   * string representativo del error.
   *
   * @param files Un array de archivos provenientes de un Dropzone
   * @param docTypeId OPCIONAL, Si no se pasa va a filtrar contra todos los
   * tipos aceptados por el uploader. Si se pasa, solamente va a filtrar con el
   * id especificado (Siempre que éste exista entre los tipos del uploader
   * actual).
   * @returns
   */
  protected filterAcceptedFiles(
    files: File[],
    shouldNotify = true,
    docTypeId?: string | TDocType,
  ): File[] {
    const store = this.getStore();

    const docTypes =
      docTypeId !== undefined
        ? store.allowedTypes.filter((current) => {
            return typeof docTypeId === 'string'
              ? current.id === docTypeId
              : current.id === docTypeId.id;
          })
        : store.allowedTypes;

    if (!docTypes.find((current) => current.docExts === '')) {
      const notAllowedFilesBecauseOfExtension: File[] = [];
      const notAllowedFilesBecauseOfSize: File[] = [];
      let maxSize = Infinity;
      const allowedFiles = files.filter((currentFile) => {
        const extension = (currentFile.name.match(/\.(\w+)$/) ?? [])[1];

        let isAllowed = true;
        if (
          !docTypes.find((currentDocType) => {
            if (
              !currentDocType.docExts
                .split(';')
                .map((current) => current.toLowerCase())
                .includes(extension.toLowerCase())
            ) {
              return false;
            }
            return true;
          })
        ) {
          isAllowed = false;
          notAllowedFilesBecauseOfExtension.push(currentFile);
        }
        if (
          isAllowed &&
          !docTypes.find((currentDocType) => {
            if (Number(currentDocType.maxSize) < currentFile.size) {
              maxSize = Number(currentDocType.maxSize);
              return false;
            }
            return true;
          })
        ) {
          notAllowedFilesBecauseOfSize.push(currentFile);
          isAllowed = false;
        }

        if (!isAllowed) return false;

        return true;
      });

      if (shouldNotify) {
        let errorMessage = '';

        if (notAllowedFilesBecauseOfExtension.length > 0) {
          const filesNames = notAllowedFilesBecauseOfExtension.reduce(
            (accumulated, current) => {
              if (accumulated.length === 0) return current.name;
              return `${accumulated}, ${current.name}`;
            },
            '',
          );

          errorMessage = `<strong>${window.FILES_WRONG_EXT}</strong><br />${filesNames}`;
        }

        if (notAllowedFilesBecauseOfSize.length > 0) {
          const filesNames = notAllowedFilesBecauseOfSize.reduce(
            (accumulated, current) => {
              if (accumulated.length === 0) return current.name;
              return `${accumulated}, ${current.name}`;
            },
            '',
          );

          if (errorMessage.length > 0)
            errorMessage = `${errorMessage} <br /><br />`;
          errorMessage = `${errorMessage}${formatMessage(window.FILES_TOO_BIG, {
            TOK1: `<br />${filesNames}`,
            TOK2: `${parseAsSize(maxSize)}`,
          })}`;
        }

        if (errorMessage.length > 0)
          this.notify({
            message: errorMessage,
            type: 'warning',
          });
      }

      return allowedFiles;
    }
    return files;
  }

  /**
   * Este método filtra del listado de archivos que se están
   * agregando, aquellos que ya hayan sido subidos previamente.
   */
  protected filterAlreadyUploadedFiles(
    files: File[],
    conf?: TSaveDroppedFilesConf,
  ): File[] {
    const store = this.getValidationStore();

    const versioningFile = store?.versioningFile;
    if (versioningFile) return files;

    const uploadedFiles = this.getStore().filesArray;
    const existingFiles: File[] = [];
    const nonExistingFiles: File[] = [];
    if (conf?.langId) {
      const alreadyUploadedToTranslationsFile = Object.values(
        this.getStore().translatedFiles[
          conf.translatingFile?.docId ?? Infinity
        ] ?? {},
      ).find((current) => (current.docName || current.name) === files[0]?.name);
      if (alreadyUploadedToTranslationsFile) existingFiles.push(files[0]);
    } else
      files.forEach((file) => {
        if (
          uploadedFiles.find(
            (search) => (search.docName ?? search.name) === file.name,
          )
        ) {
          existingFiles.push(file);
        } else nonExistingFiles.push(file);
      });
    if (existingFiles.length > 0) {
      void this.notify({
        message: `${window.LBL_DROP_FILE_REPEATED}: ${existingFiles
          .map((current) => current.name)
          .join(', ')}`,
        type: 'warning',
      });
      return nonExistingFiles;
    }
    return files;
  }

  /**
   * Este método controla que no se suban más archivos que
   * los permitidos por el parámetro this.maxFiles, tomando
   * en cuenta los archivos que ya están en la lista.
   */
  protected filterByFilesAmmountLimit(files: File[]): File[] {
    return files.slice(
      0,
      addBoundary(
        this.maxFiles - (this.getValidationStore().shownFiles?.length ?? 0),
        0,
      ),
    );
  }

  /**
   * Este método elimina del listado de archivos que
   * se agregaron al modal aquellos que ya estén en el
   * mismo.
   */
  protected filterExistingFiles(
    files: File[],
    conf?: TSaveDroppedFilesConf,
  ): File[] {
    const store = this.getStore();

    if (conf?.langId) return files;

    const currentFiles = store.inProgressFiles;
    const alreadyUploadedFiles = store.filesArray.filter(
      (current) => Number(current.docId) < 0,
    );
    let alreadyIncludedFileNames = '';
    const filteredFiles = files.filter((current) => {
      const isIncluded =
        currentFiles.findIndex((search) => search.name === current.name) !==
          -1 ||
        alreadyUploadedFiles.findIndex(
          (search) => search.docName === current.name,
        ) !== -1;

      if (isIncluded)
        alreadyIncludedFileNames = `${alreadyIncludedFileNames}${
          alreadyIncludedFileNames.length > 0 ? ', ' : ''
        }${current.name}`;

      return !isIncluded;
    });
    if (alreadyIncludedFileNames.length > 0)
      this.notify({
        message: `${window.LBL_DROP_FILE_REPEATED}: ${alreadyIncludedFileNames}`,
        type: 'warning',
      });
    return filteredFiles;
  }

  /**
   * Se utiliza para decidir si se está versionando un
   * archivo, en ese caso solamente se va a aceptar
   * uno de los archivos colocados, y será aquel que
   * tenga el mismo nombre que el archivo que se está
   * versionando.
   */
  protected filterVersioningFiles(
    files: File[],
    conf?: {
      langId?: number;
      strictMode?: boolean;
      translatingFile?: TUploaderFileInfo;
      shouldReset?: boolean;
    },
  ): File[] {
    const store = this.getValidationStore();
    const versioningFile = store?.versioningFile;
    if (versioningFile) {
      const newFiles = files.filter(
        (current) =>
          current.name === versioningFile.docName ||
          current.name === versioningFile.name,
      );
      if (newFiles.length === 0) {
        this.notify({
          type: 'warning',
          message: window.LBL_DROP_FILE_NAMES_NOT_MATCH,
        });
      }
      return newFiles;
    }
    if (
      files.length === 1 &&
      conf?.translatingFile === undefined &&
      conf?.langId === undefined
    ) {
      const rStore = this.getStore();
      const actualVersioningFile = rStore.filesArray.find(
        (current) => (current.name || current.docName) === files[0].name,
      );
      if (actualVersioningFile) {
        if (actualVersioningFile.isLockedByMe)
          this.updateModal({ versioningFile: actualVersioningFile });
      }
    }

    return files;
  }

  protected getAjaxUrl(): string {
    return window.URL_REQUEST_AJAX;
  }

  /**
   * Los métodos que comienzan en get están ideados para ser reemplazados por
   * aquellas clases que pretendan implementar un uploader distinto al de
   * documentos de proceso y documentos de entidad.
   *
   * Su función es permitir el pasaje de parámetros personalizados a los
   * requests llamados durante el proceso.
   */
  protected getAllowedTypesData(): {
    queryData: Record<string, unknown>;
    postData: Record<string, unknown>;
  } {
    return {
      queryData: {
        elemType: this.type,
        frmOut: !this.isModalOpen,
      },
      postData: {
        docTypeId: null,
        forDropElem: true,
        useDocTypePermitted: true,
        docTypePermittedObjId: this.docTypePermittedObjId,
        docTypePermittedObjType: this.type,
      },
    };
  }

  /**
   * Obtiene el tipo de documento actual considerando los archivos que se
   * desean subir
   *
   * @param unprocessedFiles
   * @param strict
   * @returns
   */
  getCurrentDocType() {
    const validationsStore = this.getValidationStore();
    return validationsStore.cmbDocType;
  }

  async getDocumentInfo(
    {
      docId: apiId,
      downloadDocId: docId,
    }: TRequireOnlyOne<{ docId: string; downloadDocId: string }>,
    newElem?: boolean,
  ) {
    const id = apiId ? this.indexedFiles[apiId].docId : docId;
    const result = await ApiaApi.post<TDocumentInformation>(
      makeApiaUrl(
        this.getDocumentInfoParameters({
          action: 'getDocumentInfo',
          isAjax: true,
          docId: id,
          newElem,
          prefix: this.type,
          ajaxUrl: this.getAjaxUrl(),
        }),
      ),
      {
        notificationsCategory: uploaderNotificationsCategory,
      },
    );

    if (result?.data) {
      return result.data;
    }

    return null;
  }

  /**
   * Devuelve el store de Redux del uploader, se usa para almacenar la
   * información necesaria independientemente si el modal está o no abierto.
   */
  getStore() {
    return globalStore.getState().fileUploadSlice.uploaders[this.id];
  }

  get modalStoreId() {
    return `DocumentsUploaderModal__${this.id}`;
  }

  /**
   * Devuelve el store que controla la vista del modal
   */
  getActualValidationStore() {
    return outerValidationsStore.getFormStore(this.modalStoreId);
  }

  /**
   * Devuelve un proxy que permite acceder a las distintas propiedades almacenadas en el store que controla el modal.
   */
  getValidationStore() {
    const storeId = this.modalStoreId;

    if (!outerValidationsStore.getFormStore(storeId))
      outerValidationsStore.registerForm(storeId);
    return new Proxy<Partial<TUploadModalStore>>({} as TUploadModalStore, {
      get(_, prop: string) {
        return outerValidationsStore.getFieldStore(storeId, prop)
          ?.value as TUploadModalStore;
      },
    });
  }

  /**
   * Devuelve un proxy que permite modificar los
   * archivos por su id.
   */
  get indexedFiles() {
    return new Proxy(
      cloneDeep(
        globalStore.getState().fileUploadSlice.uploaders[this.id].files,
      ),
      {
        get: (files, prop) => {
          if (prop in files) return files[prop as string];

          const translatedFiles = Object.values(
            this.getStore().translatedFiles,
          );
          for (let i = 0; i < translatedFiles.length; i++) {
            const translatedLangs = Object.values(translatedFiles[i]);
            for (let j = 0; j < translatedLangs.length; j++) {
              if (translatedLangs[j].docId === prop) return translatedLangs[j];
            }
          }

          return undefined;
        },
        set: (_, __, value: TUploaderFileInfo) => {
          globalStore.dispatch(
            uploaderActions.updateFile({ id: this.id, ...value }),
          );
          return true;
        },
      },
    );
  }

  /**
   * Este método está hecho para ser llamado una vez que el uploader está listo
   * para trabajar.
   */
  async initialLoad(): Promise<void> {
    await this.loadCurrentDocuments();
    if (!window.IS_READONLY) {
      await this.ajaxUploadStart();
    }
  }

  get isModalOpen() {
    return globalStore.getState().remoteModalsSlice.modals.documentUploadModal
      ?.isOpen;
  }

  maxFiles = Infinity;

  protected _type: string;

  /**
   * Carga el listado de archivos correspondientes al uploader actual.
   */
  async loadCurrentDocuments() {
    const result = await ApiaApi.post<
      TApiaLoad<TApiaFunction<TUploaderLoadCurrentFunction, false>>
    >(
      makeApiaUrl({
        ajaxUrl: this.getAjaxUrl(),
        action: 'ajaxLoadCurrent',
        isAjax: true,
        readOnly: window.IS_READONLY,
        allowLock: true,
        allowSign: true,
        allowMultiple: true,
        prefix: this.type,
      }),
      {
        notificationsCategory: uploaderNotificationsCategory,
      },
    );

    if (result && result.data) {
      const currentConfiguration: TUploaderLoadCurrentFunctionMessages =
        arrayOrArray(result.data.function.messages.message).reduce(
          (prev, { name, label }) => ({ ...prev, [name]: Boolean(label) }),
          {} as TUploaderLoadCurrentFunctionMessages,
        );

      const currentFiles: TUploaderFileInfo[] = result.data.function.general
        ? arrayOrArray(result.data.function.general).map(
            this.parseFileDefinition,
          )
        : [];

      this.currentConfiguration = currentConfiguration;
      const nonTranslationFiles: TUploaderFileInfo[] = [];
      const translationFiles: TUploaderFileInfo[] = [];
      currentFiles.forEach((current) => {
        if (current.docLang === undefined) nonTranslationFiles.push(current);
        else translationFiles.push(current);
      });
      this.filesActions.add(nonTranslationFiles);
      translationFiles.forEach((current) => {
        const originalFile = nonTranslationFiles.find(
          (search) => search.docLangGroup === current.docLangGroup,
        );
        if (originalFile && current.docLang !== undefined)
          this.setTranslationFile(
            current,
            originalFile.docId,
            Number(current.docLang),
          );
        else
          console.warn(
            `The following file is translated but has no orignal file`,
            current,
            nonTranslationFiles,
          );
      });
    }
  }

  /**
   * Intenta bloquear o desbloquear el documento.
   */
  async lockDocument(id: string) {
    if (window.IN_MONITOR) {
      return;
    }

    const file = this.indexedFiles[id];
    if (file.isMarkedToSign) {
      this.notify({
        message: window.CANT_UNLOCK_MARKED_TO_SIGN,
        type: 'warning',
      });
      return;
    }
    if (!file.isLocked || !file.isLockedByMe) {
      const isLocked = await this.checkLockDocument(file.docId, false);
      if (typeof isLocked === 'string') {
        this.filesActions.update(
          this.parseFileDefinition({
            ...file,
            userLocking: isLocked,
            lock: true,
            locked: true,
            lockedBy: isLocked,
          }),
        );
      } else this.filesActions.update({ ...this.indexedFiles[id], isLocked });
    }

    const result = await ApiaApi.post<
      TApiaFunction<
        {
          data: {
            general: {
              docId: string;
              locked: boolean;
              prefix: string;
              userLocking: string;
            };
          };
        },
        false
      >
    >(
      makeApiaUrl(
        this.getLockDocumentParameters({
          action: 'lockDocument',
          ajaxUrl: this.getAjaxUrl(),
          docId: file.docId,
          lock: file.isLocked,
          isAjax: true,
          prefix: this.type,
        }),
      ),
      {
        notificationsCategory: uploaderNotificationsCategory,
      },
    );

    if (result?.data) {
      const resultFile = result.data.function?.data?.general;
      if (resultFile)
        this.indexedFiles[resultFile.docId] = this.parseFileDefinition({
          ...file,
          ...resultFile,
        });
    }
  }

  /**
   * Intenta marcar o desmarcar el documento para ser firmado.
   */
  async markFileToSign(id: string, langId?: number) {
    if (window.IN_MONITOR) {
      return;
    }

    const file = this.indexedFiles[id];
    if ((await this.checkLockDocument(file.docId)) === true) {
      const marked = await ApiaApi.post<{ markedToSign: boolean }>(
        makeApiaUrl(
          this.getMarkFileToSignParameters({
            action: 'markDocTosign',
            docId: file.docId,
            isAjax: true,
            prefix: this.type,
            ajaxUrl: this.getAjaxUrl(),
            langId,
          }),
        ),
        {
          notificationsCategory: uploaderNotificationsCategory,
        },
      );

      if (marked?.data?.markedToSign !== undefined) {
        this.filesActions.update({
          ...file,
          isMarkedToSign: marked.data.markedToSign,
        });
      }
    }
  }

  /**
   * Este método se encarga de notificar, dependiendo de
   * si el modal está abierto o no.
   */
  protected notify(notification: TDispatchedNotification) {
    ApiaUtil.instance.notifications.notify(notification);
  }

  /**
   * Este método debe llamarse al cerrar el modal para realizar las limpiezas
   * correspondientes.
   */
  onCloseModal() {
    this.clearTempFiles();
    this.updateModal({
      metadata: {},
      shownFiles: [],
      cmbDocType: '1',
      userChangedDocType: false,
    });
    this.updateStore({ inProgressFiles: [] });
    globalStore.dispatch(remoteModalsActions.close(documentUploadModalId));
  }

  /**
   * Debería llamarse este método cuando se desea
   * abrir el modal desde fuera del Api, para
   * asegurarse de que se hagan las operaciones
   * necesarias.
   */
  openModal(props?: {
    langId?: number;
    translatingFile?: TUploaderFileInfo;
    shouldReset?: boolean;
  }) {
    if (props?.shouldReset !== false) this.resetModal();
    const { langId, translatingFile } = props ?? {};

    globalStore.dispatch(
      remoteModalsActions.open({
        id: documentUploadModalId,
        data: {
          api: this,
          langId,
          translatingFile,
        },
      }),
    );
  }

  openTranslationsModal(file: TUploaderFileInfo) {
    if (this.langs && Object.values(this.langs).length > 0)
      globalStore.dispatch(
        remoteModalsActions.open({
          id: translateModalId,
          data: {
            api: this,
            file,
            langs: this.langs,
            open: false,
          },
          title: window.TIT_TRANSLATION,
          size: 'xl',
        }),
      );
  }

  /**
   * Este método toma un id de un documento de Apia y lo agrega al modal.
   */
  public async pickFileById(id: string) {
    const documentInfo = await this.getDocumentInfo(
      { downloadDocId: id },
      true,
    );
    if (documentInfo) {
      const document = documentInfo.function.data.general;
      const { permissions } = documentInfo.function.data;
      this.updateModal({
        fromDirectoryFile: document,
        cmbDocType: document.docTypeId,
        docDesc: document.docDesc,
        docExpDate: document.docExpDate,
        docAllowAllType: document.docAllPoolPerm,
        docFolder: document.docFolder,
        poolsPermissions: arrayOrArray(permissions?.pool).map((current) => {
          const returnObject: IPoolsPermissions = {
            allowUpdate: current.permType === 'M',
            poolId: current.id,
            poolName: current.name,
          };
          return returnObject;
        }),
        isReadonly: !documentInfo.function.currentUsrPems.usrCanModify,
        isReadonlyDoctype: true,
        usersPermissions: arrayOrArray(permissions?.user).map((current) => {
          const returnObject: IUsersPermissions = {
            allowUpdate: current.permType === 'M',
            userId: current.id,
            userLogin: current.name,
          };
          return returnObject;
        }),
      });
      this.updateStore({
        inProgressFiles: [],
      });
      void this.reloadMetadata(
        { docId: document.docId, docTypeId: document.docTypeId },
        {
          docReuse: true,
        },
      );
    }
  }

  /**
   * Actualiza el listado de archivos que se muestra en el modal.
   */
  protected refreshShownFiles(shouldNotify = false) {
    const validationsStore = this.getValidationStore();
    const { inProgressFiles } = this.getStore();
    const newShownFiles = this.filterAcceptedFiles(
      [
        ...(validationsStore.shownFiles ?? []).filter(
          (current) =>
            !inProgressFiles.find((search) => search.name === current.name),
        ),
        ...inProgressFiles,
      ],
      shouldNotify,
      validationsStore.cmbDocType,
    );
    if (typeof newShownFiles !== 'string')
      this.updateModal({
        shownFiles: newShownFiles,
      });
  }

  /**
   * Carga la información correspondiente a la metadata del tipo de archivos
   * seleccionado.
   */
  async reloadMetadata(
    props?: Partial<{ docId: string; docTypeId: number | string }>,
    additionalProps?: Record<string, unknown>,
  ) {
    const result = await ApiaApi.post<{
      metadata: TDocumentMetadata | TDocumentMetadata[];
    }>(
      makeApiaUrl(
        this.getReloadMetadataParameters({
          ajaxUrl: this.getAjaxUrl(),
          action: 'reloadMetadata',
          isAjax: true,
          docId: props?.docId,
          docTypeId: props?.docTypeId,
          metadata: this.getConfirmDropModalMetadataString({
            metadata: Object.values(
              (this.getValidationStore().metadata?.value ?? {}) as Record<
                string,
                TDocumentMetadata
              >,
            ),
          }),
          prefix: this._type,
          ...additionalProps,
        }),
      ),
      {
        notificationsCategory: uploaderNotificationsCategory,
      },
    );

    let metadatas: TDocumentMetadata[] = [];
    let isFreeMetadata = false;
    if (result?.data && props?.docTypeId) {
      metadatas = arrayOrArray(result.data.metadata);
      isFreeMetadata =
        this.getStore().allowedTypes.find(
          (current) => current.id === props.docTypeId,
        )?.free ?? false;
    }
    this.updateModal({ metadatas, isFreeMetadata });
  }

  /**
   * Elimina un archivo del listado del modal.
   */
  removeInProgressFile(file: File) {
    const store = this.getStore();
    const newInProgressFilesArray = store.inProgressFiles.filter(
      (inProgressFile) => inProgressFile.name !== file.name,
    );
    this.updateStore({
      inProgressFiles: newInProgressFilesArray,
    });
    this.updateModal({
      shownFiles: this.getValidationStore().shownFiles?.filter(
        (inProgressFile) => inProgressFile.name !== file.name,
      ),
    });

    this.refreshShownFiles();
  }

  resetModal() {
    this.updateModal({
      additionalMetadata: {},
      allDocTypes: true,
      allowChangeDocType: true,
      cmbDocType: '1',
      docAllowAllType: window.DOCUMENT_EVERYONE_PERMISSION !== false ? 'M' : '',
      docDesc: '',
      docExpDate: '',
      docFolder: '',
      fromDirectoryFile: null,
      isFreeMetadata: false,
      isReadonly: false,
      isReadonlyDoctype: false,
      metadata: {},
      metadatas: [],
      poolsPermissions: [],
      usersPermissions: window.DOCUMENT_OWNER_PRIVILEGES
        ? [
            {
              allowUpdate: true,
              userId: window.CURRENT_USER_LOGIN,
              userLogin: window.CURRENT_USER_LOGIN,
            },
          ]
        : [],
      versioningFile: null,
    });

    if (this.#lastUploadKnownConf) {
      if (this.#lastUploadKnownConf.function.metadatas) {
        const metadatas = arrayOrArray(
          this.#lastUploadKnownConf.function.metadatas.metadata,
        );
        this.updateModal({ metadatas });
      }

      const allowedTypes = arrayOrArray(
        this.#lastUploadKnownConf.function.docTypes.docType,
      );

      const hasGeneric = allowedTypes.find(
        (current) => String(current.id) === '1',
      );

      const cmbDocType = hasGeneric ?? allowedTypes[0];
      const { versioningFile } = this.getValidationStore();
      if (versioningFile)
        this.updateModal({
          cmbDocType: versioningFile.docTypeId,
        });
      else if (!this.getValidationStore().userChangedDocType) {
        this.updateModal({
          cmbDocType: cmbDocType.id,
        });
      }

      this.updateModal({
        allDocTypes: this.#lastUploadKnownConf.function.general.allDocTypes,
        isFreeMetadata: cmbDocType.free,
      });
      this.updateStore({
        allowedTypes,
        useDocTypePermitted:
          this.#lastUploadKnownConf.function.general.useDocTypePermitted,
      });
    }
  }

  #hasInited = false;

  /**
   * Esta función procesa los archivos seleccionados por el usuario para ser
   * subidos al servidor.
   *
   * @param unprocessedFiles Archivos provenientes de un Dropzone.
   * @param onUploadProgress Un callback que será llamado cada vez que haya
   * @param strict Si strict se pasa en true, solamente se aceptarán archivos
   * que cumplan con el tipo de archivo seleccionado en el combo del modal (Básicamente, este parámetro está puesto para ser usado dentro del modal).
   * progreso en la carga de archivos.
   */
  async saveDroppedFiles(
    unprocessedFiles: File[],
    onUploadProgress: TOnUploadProgress,
    conf?: TSaveDroppedFilesConf,
  ) {
    if (window.IN_MONITOR) {
      return;
    }

    const {
      langId,
      strictMode: isConfStrictMode = false,
      translatingFile,
    } = conf ?? {};
    if (conf?.shouldReset) this.resetModal();

    const validationsStore = this.getValidationStore();
    const isStrictMode =
      isConfStrictMode ||
      !!validationsStore.versioningFile ||
      !!validationsStore.fromDirectoryFile;

    const docType = this.getCurrentDocType();

    if (!isStrictMode && !docType) {
      this.notify({
        message: `${window.LBL_DROP_FILE_DELETED}: ${unprocessedFiles
          .map((current) => current.name)
          .join(', ')}.`,
        type: 'warning',
      });
      return;
    }

    const allowedFiles = this.filterAlreadyUploadedFiles(
      this.filterByFilesAmmountLimit(
        this.filterAcceptedFiles(
          this.filterExistingFiles(
            this.filterVersioningFiles(unprocessedFiles, conf),
            conf,
          ),
          true,
          docType as string,
        ),
      ),
      conf,
    );

    if (typeof allowedFiles === 'string')
      this.notify({
        message: allowedFiles,
        type: 'warning',
      });
    else if (allowedFiles.length > 0) {
      onUploadProgress(0);

      const formData = new FormData();
      allowedFiles.forEach((file) =>
        formData.append('fileRepository', file, file.name),
      );

      await ApiaApi.post(
        makeApiaUrl(
          this.getSaveDroppedFilesParameters({
            action: 'saveDroppedFiles',
            docId: this.getValidationStore().versioningFile?.docId,
            ajaxUrl: this.getAjaxUrl(),
            elemType: this.type,
            elemId: `prmDocumentContainter${this._type}`,
            frmOut: !this.isModalOpen,
            docTypeId: docType,
          }),
        ),
        {
          postData: formData,
          axiosConfig: {
            onUploadProgress: (ev) =>
              onUploadProgress(
                (ev.loaded * 100) / ((ev.total ?? ev.loaded) * 2),
              ),
          },
          notificationsCategory: uploaderNotificationsCategory,
        },
      );

      const ajaxUploadFileStatusParameters =
        this.getAjaxUploadFileStatusParameters({
          action: 'ajaxUploadFileStatus',
          ajaxUrl: this.getAjaxUrl(),
          delayForDrop: true,
          isAjax: true,
          prefix: this._type,
        });

      let tries = 0;
      const checkUploadResult = async () => {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
          tries += 1;
          if (tries === 10) {
            this.notify({
              message:
                'Error while checking for uploadFileStatus, too many tries',
              type: 'danger',
            });
            resolve(false);
            return;
          }
          const response = await ApiaApi.post<TAjaxUploadFileStatus>(
            makeApiaUrl(ajaxUploadFileStatusParameters),
            {
              notificationsCategory: uploaderNotificationsCategory,
            },
          );
          if (response && response.data) {
            const hasMessages =
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              arrayOrArray<any>(
                response.data?.sysMessages?.message ??
                  response.data?.sysExceptions?.exception ??
                  response.data?.exceptions?.exception ??
                  response.data.function.messages?.message,
              ).length > 0 || !!response.data.function.dropLastMessage;

            const uploadMessage = arrayOrArray(
              response.data.function.messages?.message,
            ).find((current) => current.name === 'message');
            if (uploadMessage)
              this.notify({ message: uploadMessage.label, type: 'warning' });

            const method = response.data?.function.name;

            if (hasMessages) {
              resolve(false);
              return;
            }

            if (method === 'fncProceedDocumentProcessing') {
              resolve(true);
              return;
            }
            if (tries === 10) {
              resolve(method === 'fncProceedDocumentProcessing');
            }
            setTimeout(() => {
              checkUploadResult()
                .then(resolve)
                .catch(() => resolve(false));
            }, 100);
          } else {
            resolve(false);
          }
        });
      };

      const hasUploadedCorrectly = await checkUploadResult();

      onUploadProgress(66);

      if (hasUploadedCorrectly) {
        const shown = (validationsStore.shownFiles ?? []).map(
          (file) => file.name,
        );
        const postData = QueryString.stringify(
          this.getProcessDroppedFilesPostdata({
            useDocTypePermitted: true,
            docTypePermittedObjId: this.docTypePermittedObjId?.toString(),
            docTypePermittedObjType: this.type,
            dropped: allowedFiles.map((file) => file.name).concat(shown),
            shown,
          }),
          { arrayFormat: 'repeat' },
        );

        const secondResult = await ApiaApi.post<TProcessDroppedFiles>(
          makeApiaUrl(
            this.getProcessDroppedFilesParameters({
              action: 'processDroppedFiles',
              elemType: this.type,
              docId: this.getValidationStore().versioningFile?.docId,
              frmOut: !this.isModalOpen,
              ajaxUrl: this.getAjaxUrl(),
            }),
          ),
          {
            postData: postData.toString(),
            notificationsCategory: uploaderNotificationsCategory,
          },
        );

        onUploadProgress(83);

        if (secondResult?.data) {
          const acceptedByServerFiles = arrayOrArray(
            secondResult.data.function.principal.docInfo,
          );

          this.updateStore({
            inProgressFiles: [
              ...(this.getStore().inProgressFiles ?? []),
              ...acceptedByServerFiles
                .map((current) =>
                  [
                    ...this.getStore().inProgressFiles,
                    ...unprocessedFiles,
                  ].find((search) => search.name === current.docInfoName),
                )
                .filter((current): current is File => !!current),
            ],
          });
          this.updateModal({
            fromDirectoryFile: null,
            isReadonly: false,
          });

          this.refreshShownFiles();

          onUploadProgress(100);

          if (this.shoutSuccessAlert)
            this.notify({
              type: 'success',
              message: window.FILE_UPLOADED_SUCCESSFULLY,
            });

          if (
            isOneClickUploadEnabled(this.configurationObject.oneClickUpload)
          ) {
            if (langId && translatingFile)
              void this.confirmDropModal({ langId, translatingFile });
            else void this.confirmDropModal();
          } else if (!this.isModalOpen)
            this.openModal({
              langId,
              translatingFile,
              shouldReset: false,
            });
        }
      }

      onUploadProgress(100);
    }
  }

  /**
   * Cuando se está dentro del modal de upload, si el usuario cambia el combo
   * indicando que quiere usar un tipo distinto de archivo, este método se
   * encarga de realizar las comprobaciones correspondientes para informar al
   * usuario en caso de que alguno de los archivos no sea admitido por el nuevo
   * tipo de archivo.
   *
   * @param docTypeId
   */
  setCurrentDocType(param: number | string | TDocType) {
    const docTypeId =
      typeof param === 'string' || typeof param === 'number'
        ? param
        : param?.id;

    if (docTypeId === undefined) return;

    void this.reloadMetadata({ docTypeId }).then(() => {
      const store = this.getStore();
      const { allowedTypes } = store;
      const currentDocType = allowedTypes.find(
        (current) => current.id === docTypeId,
      );
      this.updateModal({
        cmbDocType: currentDocType?.id,
        isFreeMetadata: currentDocType?.free,
      });
      this.updateStore({ currentDocType });
      this.refreshShownFiles(true);
    });

    this.updateModal({ userChangedDocType: true });
  }

  setTranslationFile(file: TUploaderFileInfo, docId: string, langId: number) {
    globalStore.dispatch(
      uploaderActions.setTranslationFile({ id: this.id, docId, file, langId }),
    );
  }

  get type() {
    return this._type;
  }

  /**
   * Permite modificar algunas de las propiedades del modal
   */
  updateModal(props: Partial<TUploadModalStore>) {
    uploadModalMethods('updateState', props);
    outerValidationsStore.updateMultipleFields(
      this.modalStoreId,
      Object.entries(props).map(([key, value]) => {
        return { value, name: key };
      }),
    );
  }

  /**
   * Permite modificar algunas de las propiedades del store, si se quiere
   * actualizar el modal es preferible usar updateModal.
   */
  updateStore(props: Partial<Omit<IFileUploadState, 'files' | 'filesArray'>>) {
    globalStore.dispatch(uploaderActions.update({ id: this.id, ...props }));
  }

  public getLoadFilesForFolderParameters: () => Record<string, unknown> =
    () => ({
      useDocTypePermitted: true,
      docTypePermittedObjId: this.docTypePermittedObjId,
      docTypePermittedObjType: this.type,
    });

  public getLoadFileSystemStructureTree: () => Record<string, unknown> =
    () => ({
      useDocTypePermitted: true,
      docTypePermittedObjId: this.docTypePermittedObjId,
      docTypePermittedObjType: this.type,
    });

  protected getAjaxUploadFileStatusParameters = returnExactlyTheSame;

  protected getAjaxUploadStartParameters = returnExactlyTheSame;

  protected getConfirmDropModalParameters = returnExactlyTheSame;

  protected getCheckLockDocumentParameters = returnExactlyTheSame;

  protected getCheckWebDavLockParameters = returnExactlyTheSame;

  protected getConfirmDropModalPostdata = returnExactlyTheSame;

  protected getConfirmDropModalPermissionsString = ({
    poolsPermissions,
    usersPermissions,
  }: {
    poolsPermissions: IPoolsPermissions[];
    usersPermissions: IUsersPermissions[];
  }) => {
    let returnString = '';

    (poolsPermissions ?? []).forEach(
      (current) =>
        (returnString = `${returnString}${
          returnString.length > 0 ? '&' : ''
        }docPoolId=${current.poolId}&docPoolName=${
          current.poolName
        }&docPermType${current.poolId}=${String(current.allowUpdate)}`),
    );

    (usersPermissions ?? []).forEach(
      (current) =>
        (returnString = `${returnString}${
          returnString.length > 0 ? '&' : ''
        }docUserId=${current.userId}&docUsrLogin=${
          current.userLogin
        }&docPermType${current.userLogin}=${String(current.allowUpdate)}`),
    );

    return returnString;
  };

  protected getConfirmDropModalAdditionalMetadataString = (
    additionalMetadata: Record<string, TDocumentAdditionalMetadata>,
  ) => {
    return Object.values(additionalMetadata)
      .filter((current) => !!current?.value)
      .map((current) => `${current.name}~${current.value}`)
      .join(';');
  };

  protected getConfirmDropModalMetadataString = ({
    metadata,
  }: {
    metadata: TDocumentMetadata[];
  }) => {
    return metadata
      .filter((current) => !!current.value)
      .map((current) => `${current.id}~${current.type}~${current.value}`)
      .join(';');
  };

  protected getDeleteDocumentParameters = returnExactlyTheSame;

  protected getClearTempFilesParameters = returnExactlyTheSame;

  protected getCheckSignatureParameters = returnExactlyTheSame;

  protected getDocumentInfoParameters = returnExactlyTheSame;

  protected getDownloadMultipleDocumentsParameters = returnExactlyTheSame;

  protected getEditDocumentParameters = returnExactlyTheSame;

  protected getLockDocumentParameters = returnExactlyTheSame;

  protected getMarkFileToSignParameters = returnExactlyTheSame;

  protected getProcessDroppedFilesParameters = returnExactlyTheSame;

  protected getProcessDroppedFilesPostdata = returnExactlyTheSame;

  protected getReloadMetadataParameters = returnExactlyTheSame;

  protected getSaveDroppedFilesParameters = returnExactlyTheSame;

  public parseFileDefinition = (file: TFileUploaded): TUploaderFileInfo => {
    return {
      ...parseFileDefinition(file, false),
      canEdit: file.canEdit || file.canWrite,
      canRead: file.canRead,
    };
  };

  /**
   * Con este parámetro se levanta el modal para
   * versionar un documento.
   */
  async version(
    file: TUploaderFileInfo,
    onUploadProgress: TOnUploadProgress,
    conf: {
      newFiles?: File[];
      langId?: number;
      translatingFile?: TUploaderFileInfo;
    } = { newFiles: [] },
  ) {
    if (
      (await this.checkLockDocument(file.docId, true)) === true &&
      (await this.checkWebDavLock(file.docId))
    ) {
      this.resetModal();
      this.updateModal({
        versioningFile: file,
      });
      await this.ajaxUploadStart();

      if (file.docTypeId)
        this.updateModal({
          allowChangeDocType: false,
          cmbDocType: file.docTypeId,
        });
      await this.versionFileInfo(file);

      if (conf.newFiles && conf.newFiles.length > 0) {
        await this.saveDroppedFiles(conf.newFiles, onUploadProgress, {
          langId: conf.langId,
          translatingFile: conf.translatingFile,
          shouldReset: false,
          strictMode: true,
        });
      } else {
        await this.reloadMetadata({
          docId: file.docId,
          docTypeId: file.docTypeId,
        });
        this.openModal({
          langId: conf.langId,
          translatingFile: conf.translatingFile,
          shouldReset: false,
        });
      }
    }
  }

  async versionFileInfo(file: TUploaderFileInfo) {
    const documentInfo = await this.getDocumentInfo({ docId: file.docId });
    if (documentInfo?.function) {
      const {
        data: { general },
        metadatas,
      } = documentInfo.function;
      this.updateModal({
        docDesc: general.docDesc,
        docFolder: general.docFolder,
        docAllowAllType: general.docAllPoolPerm,
        docExpDate: general.docExpDate,
        usersPermissions: arrayOrArray(
          documentInfo.function.data.permissions?.user,
        ).map((current) => {
          const returnObject: IUsersPermissions = {
            allowUpdate: current.permType === 'M',
            userId: current.id,
            userLogin: current.name,
          };
          return returnObject;
        }),
        poolsPermissions: arrayOrArray(
          documentInfo.function.data.permissions?.pool,
        ).map((current) => {
          const returnObject: IPoolsPermissions = {
            allowUpdate: current.permType === 'M',
            poolId: current.id,
            poolName: current.name,
          };
          return returnObject;
        }),
        versioningFile: file,
        ...(metadatas !== undefined
          ? { metadatas: arrayOrArray(metadatas.metadata) }
          : []),
      });
    }
  }
}
